import { RootState } from '@Redux/store'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import FrontServiceConnection from '@App/sockets/front_service_connection_new'
import { SocketState } from '@App/sockets/types'
import UserServiceConnection from '@App/sockets/user_service_connection'

import { MODAL_LOGOUT_TYPE } from '@Redux/slices/modals/enums'
import { openModalFromSocket } from '@Redux/slices/modals/ModalSlice'
import {
  asyncLogout,
  updateRealityCheckPeriod,
  updateSessionTimeout,
  updateExcludePeriod,
  updateExcludeKYC,
  updateReadonly,
  updateIsUserBot,
  updateIsReadonlyRealityCheckPeriod,
  updateKycDocumentsCount,
  updateUserCountry,
} from '@Redux/slices/user/UserSlice'

const initialState = {
  socket_status: SocketState.Closed,
}

export const setConfigUserServiceConnection = createAsyncThunk(
  'userService/setConfig',
  async (urlFromConfig: string) => {
    UserServiceConnection.setApiUrl(urlFromConfig)
    return urlFromConfig
  },
)

export const closeUserServiceSocket = createAsyncThunk('socket/closeProxyServiceSocket', async (_, { dispatch }) => {
  UserServiceConnection.unsubscribe()
  UserServiceConnection.forceCloseSocket()
  return null
})

export const subscribeToUserService = createAsyncThunk('userService/subscribe', async (_, { dispatch, getState }) => {
  const handler = (message: any) => {
    if (!message?.data) return

    let parsed
    try {
      parsed = JSON.parse(message.data)
    } catch {
      return
    }

    if (parsed.event === 'socket_status_connection') {
      dispatch(setUserServiceStatus(parsed.status))
    }

    if (parsed.event === 'response_platform_user') {
      const userData = parsed.payload?.user?.data
      const modalErrorType: MODAL_LOGOUT_TYPE = parsed.payload?.user.error_type

      if (userData?.reality_check_period !== undefined) {
        dispatch(updateRealityCheckPeriod(userData.reality_check_period))
      }

      if (userData?.is_readonly_reality_check !== undefined) {
        dispatch(updateIsReadonlyRealityCheckPeriod(userData.is_readonly_reality_check))
      }

      if (userData?.session_timeout !== undefined) {
        dispatch(updateSessionTimeout(userData.session_timeout))
      }

      if (userData?.exclude_period !== undefined) {
        dispatch(updateExcludePeriod(userData.exclude_period))
      }

      if (userData?.exclude_kyc !== undefined) {
        dispatch(updateExcludeKYC(userData.exclude_kyc))
      }

      if (userData?.is_readonly !== undefined) {
        dispatch(updateReadonly(userData.is_readonly))
      }

      if (userData?.bot !== undefined) {
        dispatch(updateIsUserBot(userData.bot))
      }

      if (userData?.kyc_documents_status_count !== undefined) {
        dispatch(updateKycDocumentsCount(userData.kyc_documents_status_count))
      }

      if (userData?.country !== undefined) {
        dispatch(updateUserCountry(userData.country))
      }

      const state = getState() as RootState
      const isFreezeRealityCheck = state.config.is_freeze_reality_check

      if (userData?.flag_reality_check_period) {
        if (isFreezeRealityCheck) {
          dispatch(
            openModalFromSocket({
              type: 'freeze_period',
              data: {
                flag_reality_check_period: userData.flag_reality_check_period,
                period_just_seconds_real_last_time: userData.period_just_seconds_real_last_time || 0,
                reality_check_period: userData.reality_check_period,
                reality_check_last_time: userData.reality_check_last_time,
              },
            }),
          )
        } else {
          dispatch(
            openModalFromSocket({
              type: 'reality_check',
              data: {
                flag_reality_check_period: userData.flag_reality_check_period,
                period_start_session_time: userData.period_start_session_time,
                reality_check_period: userData.reality_check_period,
                reality_check_last_time: userData.reality_check_last_time,
              },
            }),
          )
        }
      }

      if (userData?.exclude_kyc) {
        dispatch(
          openModalFromSocket({
            type: 'exclude_kyc',
          }),
        )
      }

      if (userData?.flag_inactivity_notice) {
        dispatch(
          openModalFromSocket({
            type: 'inactivity_notice',
            data: {
              seconds_inactivity_notice: userData.seconds_inactivity_notice,
              flag_inactivity_notice: userData.flag_inactivity_notice,
            },
          }),
        )
      }

      if (userData?.is_new_cookie_policy_accept && userData?.need_terms_and_conditions_accept !== '') {
        dispatch(
          openModalFromSocket({
            type: 'new_terms',
            data: { need_terms_and_conditions_accept: userData.need_terms_and_conditions_accept },
          }),
        )
      }

      switch (modalErrorType) {
        case MODAL_LOGOUT_TYPE.SESSION_IS_NOT_VALID:
          dispatch(asyncLogout())
          break
        case MODAL_LOGOUT_TYPE.INACTIVITY_TIME:
          dispatch(asyncLogout())
          dispatch(openModalFromSocket({ type: MODAL_LOGOUT_TYPE.INACTIVITY_TIME }))
          break
        case MODAL_LOGOUT_TYPE.SESSION_TIMEOUT:
          dispatch(asyncLogout())
          dispatch(
            openModalFromSocket({
              type: MODAL_LOGOUT_TYPE.SESSION_TIMEOUT,
              data: { error_user_data: parsed.payload.user.error_user_data },
            }),
          )
          break
        case MODAL_LOGOUT_TYPE.CLOSE_ACCOUNT:
          dispatch(asyncLogout())
          dispatch(openModalFromSocket({ type: MODAL_LOGOUT_TYPE.CLOSE_ACCOUNT }))
          break
        case MODAL_LOGOUT_TYPE.CLOSE_MULTIPLE_ACCOUNT:
          dispatch(asyncLogout())
          dispatch(openModalFromSocket({ type: MODAL_LOGOUT_TYPE.CLOSE_MULTIPLE_ACCOUNT }))
          break
        default:
          break
      }
    }
  }

  UserServiceConnection.subscribe((message: any) => {
    handler(message)
  })

  return null
})
export const setUserServiceStatus = createAsyncThunk('userService/setStatus', async (status: SocketState) => status)

export const UserServiceConnectionSlice = createSlice({
  name: 'user_service',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setUserServiceStatus.fulfilled, (state, action) => {
      state.socket_status = action.payload
    })
  },
})

export default UserServiceConnectionSlice.reducer
